import { v4 as uuid } from 'uuid'
import {
  getEncounterTimelineData,
  getRaidPlanAbilities,
} from 'app/frontend/base/services/raidPlanData'
import {
  getClass,
  getSpecialization,
} from 'app/frontend/base/services/characterValues'
import RaidPlan, {
  RaidPlanActor,
  RaidPlanAssignmentEntity,
  RaidPlanEncounterAssignmentAbility,
  RaidPlanTimeline,
} from 'app/frontend/types/custom/RaidPlan'
import { resolvePhaseTiming } from '../utils/phaseTiming'
import {
  AssignmentViewMode,
  getAssignmentScopeIndexes,
  normalizeAssignmentCast,
  normalizeCustomGrouping,
} from './assignmentTimeline'
import getAssignmentEntityIdentity from './assignmentEntityIdentity'

type ImportFromEventActivityNoteErrorCode =
  | 'invalid_note'
  | 'encounter_mismatch'
  | 'no_supported_lines'

type ImportFromEventActivityNoteResult =
  | { timeline: RaidPlanTimeline }
  | { errorCode: ImportFromEventActivityNoteErrorCode }

type ImportFromEventActivityNoteOptions = {
  note: string
  raidPlan: RaidPlan
  encounterWclId?: number
  timeline: RaidPlanTimeline
  rosterMemberships: wowaudit.TeamMembership[]
}

type ParsedTimedLine = {
  at: number
  phaseIndex: number
  spellId: number | null
  bossSpellId: number | null
  tag: string | null
  text: string | null
  ttsMessage: string | null
  rgb: string | null
}

type AssignmentLine = {
  at: number
  phaseIndex: number
  abilityGroupKey: string
  spellId: number | null
  title: string | null
  text: string | null
  ttsMessage: string | null
  rgb: string | null
  entities: RaidPlanAssignmentEntity[]
}

const NOTE_ASSIGNMENT_SPELL_ID = 136243
const ROLE_TOKENS = new Set([
  'tank',
  'healer',
  'dps',
  'melee',
  'ranged',
  'anyone',
])

const normalizeToken = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')

const normalizeTextForMatch = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9 ]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

const parseNumber = (value: string | undefined) => {
  if (!value) return null
  const parsed = Number(value)
  if (!Number.isFinite(parsed)) return null
  return parsed
}

const toRgb = (value: string | undefined) => {
  if (!value) return null
  const parts = value
    .trim()
    .split(/\s+/)
    .map((token) => Number(token))
  if (parts.length < 3 || parts.some((entry) => !Number.isFinite(entry))) {
    return null
  }
  const [red, green, blue] = parts
  const toChannel = (channel: number) =>
    Math.max(0, Math.min(Math.round(channel * 255), 255))
  return `rgb(${toChannel(red)}, ${toChannel(green)}, ${toChannel(blue)})`
}

const parseProperties = (line: string) => {
  const cleaned = line.trim().replace(/;$/, '')
  const properties: Record<string, string> = {}
  cleaned.split(';').forEach((token) => {
    const trimmed = token.trim()
    if (!trimmed.length) return
    const separatorIndex = trimmed.indexOf(':')
    if (separatorIndex <= 0) return
    const key = trimmed.slice(0, separatorIndex).trim()
    const value = trimmed.slice(separatorIndex + 1).trim()
    if (!key.length) return
    properties[key] = value
  })
  return properties
}

const parseEncounterSections = (note: string) => {
  const sections: Array<{
    encounterId: number | null
    encounterName: string | null
    lines: string[]
  }> = []
  let activeSectionIndex = -1
  note.split('\n').forEach((rawLine) => {
    const line = rawLine.trim()
    if (!line.length) return
    if (line.startsWith('EncounterID:')) {
      const properties = parseProperties(line)
      const encounterId = parseNumber(properties.EncounterID)
      const encounterName = properties.Name?.trim() || null
      sections.push({
        encounterId:
          encounterId !== null && Number.isInteger(encounterId)
            ? encounterId
            : null,
        encounterName,
        lines: [],
      })
      activeSectionIndex = sections.length - 1
      return
    }
    if (activeSectionIndex < 0) return
    sections[activeSectionIndex].lines.push(line)
  })
  return sections
}

const buildAbilityLookups = () => {
  const raidPlanAbilities = getRaidPlanAbilities()
  const specsBySpellId = Object.entries(raidPlanAbilities.specSpells).reduce<
    Record<number, number[]>
  >((acc, [specIdRaw, spells]) => {
    const specId = Number(specIdRaw)
    spells.forEach((spell) => {
      if (!acc[spell.id]) acc[spell.id] = []
      acc[spell.id].push(specId)
    })
    return acc
  }, {})
  const classesBySpellId = Object.entries(raidPlanAbilities.classSpells).reduce<
    Record<number, number[]>
  >((acc, [classIdRaw, spells]) => {
    const classId = Number(classIdRaw)
    spells.forEach((spell) => {
      if (!acc[spell.id]) acc[spell.id] = []
      acc[spell.id].push(classId)
    })
    return acc
  }, {})
  return { specsBySpellId, classesBySpellId }
}

const inferCustomGrouping = (groupSizes: number[]) => {
  if (groupSizes.length < 2) return undefined
  const hasNonDefault = groupSizes.some((size) => size !== 1)
  if (!hasNonDefault) return undefined
  for (
    let patternSize = 1;
    patternSize <= groupSizes.length;
    patternSize += 1
  ) {
    const pattern = groupSizes.slice(0, patternSize)
    const isPatternMatch = groupSizes.every(
      (value, index) => value === pattern[index % pattern.length],
    )
    if (!isPatternMatch) continue
    if (pattern.length === 1) return undefined
    return normalizeCustomGrouping(pattern.join(' ')) ?? undefined
  }
  return undefined
}

const inferViewMode = (
  casts: NonNullable<RaidPlanEncounterAssignmentAbility['casts']>,
  customGrouping?: string,
): AssignmentViewMode => {
  if (casts.length <= 1) return 'all_together'
  const castSignatures = casts.map((cast) =>
    cast.boxes
      .map((box) =>
        [...box.entities]
          .map((entity) => getAssignmentEntityIdentity(entity))
          .sort((left, right) => left.localeCompare(right))
          .join(','),
      )
      .join('|'),
  )
  const castEntries = casts.map((_, index) => ({ time: index }))
  const castGroups = getAssignmentScopeIndexes(
    castEntries,
    'all_separate',
    customGrouping,
  ).map((scope) => scope.castIndexes)
  const groupSignatures = castGroups.map((castIndexes) =>
    castIndexes.map((castIndex) => castSignatures[castIndex]).join('||'),
  )
  if (new Set(groupSignatures).size === 1) {
    return 'all_together'
  }
  const alternatingScopes = getAssignmentScopeIndexes(
    castEntries,
    'alternating',
    customGrouping,
  )
  if (alternatingScopes.length === 2) {
    const oddSignatures = new Set(
      alternatingScopes[0].castIndexes.map(
        (castIndex) => castSignatures[castIndex],
      ),
    )
    const evenSignatures = new Set(
      alternatingScopes[1].castIndexes.map(
        (castIndex) => castSignatures[castIndex],
      ),
    )
    if (
      oddSignatures.size === 1 &&
      evenSignatures.size === 1 &&
      oddSignatures.values().next().value !==
        evenSignatures.values().next().value
    ) {
      return 'alternating'
    }
  }
  return 'all_separate'
}

const mapTagTokenToEntity = ({
  token,
  rosterByName,
}: {
  token: string
  rosterByName: Record<string, wowaudit.TeamMembership>
}): RaidPlanAssignmentEntity | null => {
  const normalized = normalizeToken(token)
  if (!normalized.length) return null
  if (normalized === 'everyone') {
    return { kind: 'everyone' }
  }
  if (ROLE_TOKENS.has(normalized)) {
    return {
      kind: 'by_role',
      role: normalized as
        | 'anyone'
        | 'tank'
        | 'healer'
        | 'dps'
        | 'ranged'
        | 'melee',
    }
  }
  const groupMatch = normalized.match(/^group([1-8])$/)
  if (groupMatch) {
    return {
      kind: 'group',
      group: Number(groupMatch[1]) as 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8,
    }
  }
  const membership = rosterByName[normalized]
  if (!membership) return null
  return {
    kind: 'member',
    character_id: membership.characterReference.id,
    spec_id: membership.specId,
    role: membership.role,
    label: membership.characterReference.name,
  }
}

const buildAssignmentAbilities = ({
  assignmentLines,
  encounterAbilityEntryIdsBySpellId,
}: {
  assignmentLines: AssignmentLine[]
  encounterAbilityEntryIdsBySpellId: Record<number, string[]>
}) => {
  const grouped = assignmentLines.reduce<Record<string, AssignmentLine[]>>(
    (acc, line) => {
      if (!acc[line.abilityGroupKey]) acc[line.abilityGroupKey] = []
      acc[line.abilityGroupKey].push(line)
      return acc
    },
    {},
  )
  const orderedKeys = Object.keys(grouped).sort((leftKey, rightKey) => {
    const leftFirst = grouped[leftKey][0]?.at ?? 0
    const rightFirst = grouped[rightKey][0]?.at ?? 0
    if (leftFirst !== rightFirst) return leftFirst - rightFirst
    return leftKey.localeCompare(rightKey)
  })
  const abilityOrder: string[] = []
  const abilities: Record<string, RaidPlanEncounterAssignmentAbility> = {}

  orderedKeys.forEach((groupKey) => {
    const lines = [...grouped[groupKey]].sort((left, right) => {
      if (left.at !== right.at) return left.at - right.at
      return (left.title ?? '').localeCompare(right.title ?? '')
    })
    const groupedByTime = lines.reduce<
      Array<{ at: number; phaseIndex: number; lines: AssignmentLine[] }>
    >((acc, line) => {
      const current = acc[acc.length - 1]
      if (current && current.at === line.at) {
        current.lines.push(line)
        return acc
      }
      acc.push({
        at: line.at,
        phaseIndex: line.phaseIndex,
        lines: [line],
      })
      return acc
    }, [])
    const assignmentGroups = Math.max(
      1,
      ...groupedByTime.map((entry) => entry.lines.length),
    )
    const casts = groupedByTime.map((entry) =>
      normalizeAssignmentCast(
        {
          boxes: Array.from({ length: assignmentGroups }, (_, boxIndex) => {
            const line = entry.lines[boxIndex]
            if (!line) {
              return { entities: [] }
            }
            return {
              entities: line.entities,
            }
          }),
        },
        assignmentGroups,
      ),
    )
    const customGrouping = inferCustomGrouping(
      groupedByTime.map((entry) => entry.lines.length),
    )
    const viewMode = inferViewMode(casts, customGrouping)
    const firstLine = lines[0]
    if (!firstLine) return

    const castOverrides: NonNullable<
      RaidPlanEncounterAssignmentAbility['cast_overrides']
    > = {}
    if (firstLine.spellId !== null) {
      ;(encounterAbilityEntryIdsBySpellId[firstLine.spellId] ?? []).forEach(
        (id) => {
          castOverrides[id] = { ignored: true }
        },
      )
    }
    groupedByTime.forEach((entry, index) => {
      castOverrides[`import_${index}`] = {
        custom: true,
        phase_index: entry.phaseIndex,
        time: entry.at,
      }
    })

    const abilityKey =
      firstLine.spellId !== null
        ? firstLine.spellId.toString()
        : uuid().replaceAll('-', '_')
    abilityOrder.push(abilityKey)
    abilities[abilityKey] = {
      spell_id: firstLine.spellId ?? NOTE_ASSIGNMENT_SPELL_ID,
      casts,
      assignment_groups: assignmentGroups,
      message: firstLine.text ?? undefined,
      tts_message: firstLine.ttsMessage ?? undefined,
      rgb: firstLine.rgb ?? undefined,
      view_mode: viewMode,
      custom_grouping: customGrouping,
      title:
        firstLine.spellId === null ? firstLine.title ?? undefined : undefined,
      cast_overrides: castOverrides,
      raid_plan_link: undefined,
    }
  })

  if (!abilityOrder.length) return undefined
  return {
    abilities,
    ability_order: abilityOrder,
  }
}

const buildActors = ({
  castsByTag,
  rosterByName,
  specsBySpellId,
  classesBySpellId,
}: {
  castsByTag: Record<string, ParsedTimedLine[]>
  rosterByName: Record<string, wowaudit.TeamMembership>
  specsBySpellId: Record<number, number[]>
  classesBySpellId: Record<number, number[]>
}) => {
  const actors: Array<
    RaidPlanActor & {
      actorName: string
      classKey: string
    }
  > = []

  Object.entries(castsByTag).forEach(([tag, casts]) => {
    const normalizedTag = normalizeToken(tag)
    const rosterMember = rosterByName[normalizedTag]
    const specVotes: Record<number, number> = {}
    const classVotes: Record<number, number> = {}
    casts.forEach((cast) => {
      if (cast.spellId === null) return
      ;(specsBySpellId[cast.spellId] ?? []).forEach((specId) => {
        specVotes[specId] = (specVotes[specId] ?? 0) + 1
      })
      ;(classesBySpellId[cast.spellId] ?? []).forEach((classId) => {
        classVotes[classId] = (classVotes[classId] ?? 0) + 1
      })
    })
    const resolvedSpecId =
      Object.entries(specVotes).sort(
        (left, right) => Number(right[1]) - Number(left[1]),
      )[0]?.[0] ?? null
    const specId = resolvedSpecId !== null ? Number(resolvedSpecId) : null
    const classFromSpec =
      specId !== null
        ? getSpecialization(specId.toString())?.class_id ?? null
        : null
    const resolvedClassId =
      classFromSpec ??
      Object.entries(classVotes).sort(
        (left, right) => Number(right[1]) - Number(left[1]),
      )[0]?.[0] ??
      null
    const classId =
      resolvedClassId !== null
        ? Number(resolvedClassId)
        : rosterMember?.characterReference.classId ?? null
    if (classId === null) return
    const rosterMatchesClass =
      rosterMember?.characterReference.classId === classId &&
      (specId === null || rosterMember.specId === specId)
    const preferredCharacterId = rosterMatchesClass
      ? rosterMember.characterReference.id
      : undefined
    const actorCasts = casts
      .filter((cast) => cast.spellId !== null)
      .sort((left, right) => left.at - right.at)
      .map((cast) => ({
        id: uuid(),
        spell_id: cast.spellId,
        at: cast.at,
        message: cast.text ?? undefined,
        tts_message: cast.ttsMessage ?? undefined,
        rgb: cast.rgb ?? undefined,
      }))
    const classLabel = getClass(classId.toString()) ?? `Class ${classId}`
    const specLabel =
      specId !== null
        ? getSpecialization(specId.toString())?.name ?? classLabel
        : classLabel
    actors.push({
      uuid: uuid(),
      spec_id: specId,
      class_id: classId,
      preferred_character_id: preferredCharacterId,
      label: specLabel,
      casts: actorCasts,
      actorName: tag,
      classKey: `${classId}:${specId ?? 'none'}`,
    })
  })

  const groupedClassSpecCounts = actors.reduce<Record<string, number>>(
    (acc, actor) => {
      acc[actor.classKey] = (acc[actor.classKey] ?? 0) + 1
      return acc
    },
    {},
  )
  const groupedClassSpecIndex: Record<string, number> = {}

  return actors
    .sort((left, right) => left.actorName.localeCompare(right.actorName))
    .map((actor) => {
      groupedClassSpecIndex[actor.classKey] =
        (groupedClassSpecIndex[actor.classKey] ?? 0) + 1
      const suffix =
        groupedClassSpecCounts[actor.classKey] > 1
          ? ` ${groupedClassSpecIndex[actor.classKey]}`
          : ' 1'
      return {
        uuid: actor.uuid,
        spec_id: actor.spec_id,
        class_id: actor.class_id,
        preferred_character_id: actor.preferred_character_id,
        label: `${actor.label}${suffix}`,
        casts: actor.casts,
      }
    })
}

export const importFromEventActivityNote = ({
  note,
  raidPlan,
  encounterWclId,
  timeline,
  rosterMemberships,
}: ImportFromEventActivityNoteOptions): ImportFromEventActivityNoteResult => {
  const sections = parseEncounterSections(note)
  if (!sections.length) {
    return { errorCode: 'invalid_note' }
  }
  const encounterTimeline =
    getEncounterTimelineData()[raidPlan.encounterId.toString()]
  const normalizeEncounterName = (value: string) =>
    value
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '')
  const matchingSectionByWclId =
    encounterWclId !== undefined
      ? sections.find((section) => section.encounterId === encounterWclId)
      : undefined
  const matchingSectionById = sections.find(
    (section) => section.encounterId === raidPlan.encounterId,
  )
  const matchingSectionByName = encounterTimeline
    ? sections.find(
        (section) =>
          section.encounterName !== null &&
          normalizeEncounterName(section.encounterName) ===
            normalizeEncounterName(encounterTimeline.name),
      )
    : undefined
  const matchingSection =
    matchingSectionByWclId ?? matchingSectionById ?? matchingSectionByName
  if (!matchingSection) {
    return { errorCode: 'encounter_mismatch' }
  }
  const encounterDifficulty =
    encounterTimeline?.difficulties.find(
      (difficulty) => difficulty.difficulty === raidPlan.difficulty,
    ) ?? encounterTimeline?.difficulties[0]
  if (!encounterDifficulty) {
    return { errorCode: 'invalid_note' }
  }

  const { phaseStarts, phaseEnds } = resolvePhaseTiming({
    phases: encounterDifficulty.phases,
    persistedPhaseDurations:
      timeline.phase_durations ?? raidPlan.timeline.phase_durations,
  })

  const rosterByName = (rosterMemberships ?? []).reduce<
    Record<string, wowaudit.TeamMembership>
  >((acc, membership) => {
    acc[normalizeToken(membership.characterReference.name)] = membership
    return acc
  }, {})

  const encounterAbilityNames = new Set(
    Object.values(encounterDifficulty.abilities)
      .map((ability) => normalizeTextForMatch(ability.name ?? ''))
      .filter((entry) => entry.length > 0),
  )
  const containsEncounterAbilityName = (text: string) => {
    const normalized = normalizeTextForMatch(text)
    if (!normalized.length) return false
    return Array.from(encounterAbilityNames).some((abilityName) =>
      normalized.includes(abilityName),
    )
  }

  const parsedTimedLines: ParsedTimedLine[] = matchingSection.lines
    .map((line) => {
      const properties = parseProperties(line)
      const phaseRaw = parseNumber(properties.ph)
      const relativeTime = parseNumber(properties.time)
      if (phaseRaw === null || relativeTime === null) return null
      const phaseIndex = Math.floor(phaseRaw) - 1
      const phaseStart = phaseStarts[phaseIndex]
      const phaseEnd = phaseEnds[phaseIndex]
      if (phaseStart === undefined || phaseEnd === undefined) return null
      const at = Math.max(
        phaseStart,
        Math.min(
          Math.floor(phaseStart + relativeTime),
          Math.ceil(phaseEnd) - 1,
        ),
      )
      return {
        at,
        phaseIndex,
        spellId: parseNumber(properties.spellid),
        bossSpellId: parseNumber(properties.bossSpell),
        tag: properties.tag?.trim() || null,
        text: properties.text?.trim() || null,
        ttsMessage: properties.TTS?.trim() || null,
        rgb: toRgb(properties.colors),
      } as ParsedTimedLine
    })
    .filter((entry): entry is ParsedTimedLine => entry !== null)

  if (!parsedTimedLines.length) {
    return { errorCode: 'no_supported_lines' }
  }

  const castsByTag = parsedTimedLines.reduce<Record<string, ParsedTimedLine[]>>(
    (acc, line) => {
      if (line.spellId === null || !line.tag || line.tag === 'everyone')
        return acc
      if (!acc[line.tag]) acc[line.tag] = []
      acc[line.tag].push(line)
      return acc
    },
    {},
  )

  const generalCasts = parsedTimedLines
    .filter((line) => line.tag === 'everyone')
    .filter(
      (line) =>
        line.spellId !== null ||
        Boolean(line.text?.trim()) ||
        Boolean(line.ttsMessage?.trim()),
    )
    .sort((left, right) => left.at - right.at)
    .map((line) => ({
      id: uuid(),
      spell_id: line.spellId,
      at: line.at,
      message: line.text ?? undefined,
      tts_message: line.ttsMessage ?? undefined,
      rgb: line.rgb ?? undefined,
    }))

  const assignmentLines: AssignmentLine[] = parsedTimedLines.flatMap((line) => {
    if (!line.tag) return []
    if (line.tag === 'everyone') return []
    if (line.spellId !== null) return []
    const hasCustomAssignmentCandidate =
      line.bossSpellId === null &&
      Boolean(line.text) &&
      !containsEncounterAbilityName(line.text ?? '')
    if (line.bossSpellId === null && !hasCustomAssignmentCandidate) {
      return []
    }
    const entities = line.tag.split(/\s+/).flatMap((token) => {
      const entity = mapTagTokenToEntity({
        token,
        rosterByName,
      })
      return entity ? [entity] : []
    })
    const abilityGroupKey =
      line.bossSpellId !== null
        ? `boss:${line.bossSpellId}`
        : `custom:${normalizeTextForMatch(line.text ?? '')}`
    return [
      {
        at: line.at,
        phaseIndex: line.phaseIndex,
        abilityGroupKey,
        spellId: line.bossSpellId,
        title: line.bossSpellId === null ? line.text : null,
        text: line.text,
        ttsMessage: line.ttsMessage,
        rgb: line.rgb,
        entities,
      },
    ]
  })

  const encounterAbilityEntryIdsBySpellId = Object.values(
    encounterDifficulty.abilities,
  ).reduce<Record<number, string[]>>((acc, ability) => {
    acc[ability.spell_id] = ability.entries
      .map((entry) => entry.id)
      .filter((entry): entry is string => Boolean(entry))
    return acc
  }, {})

  const encounterAssignments = buildAssignmentAbilities({
    assignmentLines,
    encounterAbilityEntryIdsBySpellId,
  })

  const { specsBySpellId, classesBySpellId } = buildAbilityLookups()
  const actors = buildActors({
    castsByTag,
    rosterByName,
    specsBySpellId,
    classesBySpellId,
  })

  const hasSupportedData =
    actors.length > 0 ||
    generalCasts.length > 0 ||
    Boolean(encounterAssignments)
  if (!hasSupportedData) {
    return { errorCode: 'no_supported_lines' }
  }

  return {
    timeline: {
      actors,
      phase_durations:
        timeline.phase_durations ?? raidPlan.timeline.phase_durations,
      general: generalCasts.length > 0 ? { casts: generalCasts } : undefined,
      encounter_assignments: encounterAssignments,
    },
  }
}

export type {
  ImportFromEventActivityNoteErrorCode,
  ImportFromEventActivityNoteResult,
}
